import { confetti } from "@tsparticles/confetti"

import { EXPERIMENTAL_ANIMATIONS } from "../config/constants"
import { prefersReducedMotion } from "./utils/prefers-reduced-motion"

const MIN_BURSTS = 6
const MAX_BURSTS = 10
const MIN_DELAY = 0
const MAX_DELAY = 900
// Later bursts wait at least this long so particles from different bursts do
// not all spawn in the same frame (measured: with 8-16 bursts × 150-350
// particles all delayed 0-600ms, the win moment produced ~400ms of jank under
// CPU throttle 4x — long tasks ~450ms, max frame gap ~490ms vs ~60ms without
// the burst).
const MIN_LATER_BURST_DELAY = 80
const VARIANTS = [
  ["star"],
  ["circle"],
  ["square"],
  ["star", "circle"],
  ["star", "square"],
  ["circle", "square"],
  ["star", "circle", "square"],
]

const randomInt = (min: number, max: number)=>
  Math.floor(Math.random() * (max - min + 1)) + min

const randomVariant = ()=> VARIANTS[randomInt(0, VARIANTS.length - 1)]

const randomSuccessColors = ()=> {
  const count = randomInt(4, 8)
  return Array.from({ length: count }, ()=> {
    const h = randomInt(40, 140)
    const s = randomInt(70, 100)
    const l = randomInt(40, 75)
    return `hsl(${h} ${s}% ${l}%)`
  })
}

const randomErrorColors = ()=> {
  const count = randomInt(3, 6)
  return Array.from({ length: count }, ()=> {
    const h = randomInt(0, 20)
    const s = randomInt(70, 100)
    const l = randomInt(30, 60)
    return `hsl(${h} ${s}% ${l}%)`
  })
}

// Haptic success cue (mobile). navigator.vibrate is only available on devices
// that support it, so calling it when present already narrows to mobile — on
// desktop browsers it either doesn't exist or is a no-op.
const SUCCESS_VIBRATION_PATTERN = [100, 50, 100, 50, 200]

const vibrateSuccess = (): void=> {
  if ("vibrate" in navigator) {
    navigator.vibrate(SUCCESS_VIBRATION_PATTERN)
  }
}

const ERROR_VIBRATION_PATTERN = [200, 100, 200, 100, 400]

const vibrateError = (): void=> {
  if ("vibrate" in navigator) {
    navigator.vibrate(ERROR_VIBRATION_PATTERN)
  }
}

export function fireErrorFeedbackCanvas(vibrate?: boolean) {
  if (vibrate) vibrateError()
  if (!EXPERIMENTAL_ANIMATIONS) return
  // Haptics above stay on for reduced-motion users (a non-visual cue); the
  // visual confetti is gated here as well as per-burst via disableForReducedMotion.
  if (prefersReducedMotion()) return

  const count = randomInt(3, 6)
  const ticks = randomInt(60, 120)

  Array.from({ length: count }).forEach(()=> {
    const x = Math.random() * 100
    const y = Math.random() * 100
    const delay = randomInt(MIN_DELAY, MAX_DELAY)

    setTimeout(()=> {
      confetti({
        angle: randomInt(0, 360),
        colors: randomErrorColors(),
        count: randomInt(80, 180),
        decay: 0.85 + Math.random() * 0.1,
        disableForReducedMotion: true,
        flat: Math.random() > 0.5,
        gravity: 0.6 + Math.random() * 0.4,
        position: { x, y },
        scalar: 0.6 + Math.random() * 1.5,
        ["shapes"]: randomVariant(),
        spread: randomInt(60, 120),
        startVelocity: randomInt(25, 55),
        ticks,
        zIndex: 9999,
      })
    }, delay)
  })
}

// Fires the success celebration. An optional `vibrate` flag additionally plays
// a haptic pulse on mobile (independent of the visual-animation flag, so a user
// who disables animations still gets the vibration cue).
export function fireSuccessFeedbackCanvas(
  durationMs?: number,
  vibrate?: boolean,
) {
  if (vibrate) vibrateSuccess()
  if (!EXPERIMENTAL_ANIMATIONS) return
  // Haptics above stay on for reduced-motion users (a non-visual cue); the
  // visual confetti is gated here as well as per-burst via disableForReducedMotion.
  if (prefersReducedMotion()) return

  const count = randomInt(MIN_BURSTS, MAX_BURSTS)
  const ticks =
    durationMs != null
      ? Math.round((durationMs / 1000) * 60)
      : randomInt(180, 400)

  Array.from({ length: count }).forEach((_, index)=> {
    const x = Math.random() * 100
    const y = Math.random() * 100
    // The FIRST burst is instant — the celebration must not feel lagged (a
    // random 0-600ms delay on every burst made the win moment visibly late).
    // Later bursts spread out so the main thread never spawns everything at once.
    const delay = index === 0 ? 0 : randomInt(MIN_LATER_BURST_DELAY, MAX_DELAY)

    setTimeout(()=> {
      confetti({
        angle: (()=> {
          if (y < 20) return randomInt(60, 120)
          if (y > 80) return randomInt(240, 300)
          return randomInt(0, 360)
        })(),
        colors: randomSuccessColors(),
        count: randomInt(100, 220),
        decay: 0.85 + Math.random() * 0.1,
        disableForReducedMotion: true,
        flat: Math.random() > 0.5,
        gravity: 0.4 + Math.random() * 0.6,
        position: { x, y },
        scalar: 0.5 + Math.random() * 1.7,
        ["shapes"]: randomVariant(),
        spread: randomInt(80, 180),
        startVelocity: randomInt(35, 75),
        ticks,
        zIndex: 9999,
      })
    }, delay)
  })
}
